.button {
	display: inline-flex;
	gap: var(--spacing-2);
	align-items: center;
	justify-content: center;
	font-family: inherit;
	font-weight: var(--font-weight-medium);
	letter-spacing: 0.02em;
	text-decoration: none;
	cursor: pointer;
	border: 1px solid transparent;
	border-radius: var(--radius-md);
	transition:
		background-color var(--motion-fast) var(--ease-standard),
		border-color var(--motion-fast) var(--ease-standard);

	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}

	/* Press feedback for every variant — nudge down on click (primary also darkens
	   its background via its own :active). */
	&:active:not(:disabled) {
		transform: translateY(1px);
	}

	/* Size — driven by the data-size prop */
	&[data-size='small'] {
		min-block-size: 2rem;
		padding: 0 var(--spacing-3);
		font-size: var(--font-size-sm);
	}

	&[data-size='medium'] {
		min-block-size: 2.5rem;
		padding: 0 var(--spacing-4);
		font-size: var(--font-size-base);
	}

	&[data-size='large'] {
		min-block-size: 3rem;
		padding: 0 var(--spacing-5);
		font-size: var(--font-size-lg);
	}

	/* Touch: a coarse pointer (touch screen / stylus) wants a ≥40px hit target, so the
	   dense `small` size grows to the standard control height. Medium (40) and large (48)
	   already clear it, so only `small` is bumped — never lowered. */
	@media (pointer: coarse) {
		&[data-size='small'] {
			min-block-size: var(--control-height);
		}
	}

	/* Variant — driven by the data-variant prop */

	/* The kiss.cat marketing CTA look: a violet gradient that lifts on hover
	   (brightness + a taller glow) and presses in on click. */
	&[data-variant='primary'] {
		color: var(--colour-text-on-brand);
		background: var(--colour-brand-gradient);
		box-shadow: var(--colour-brand-elevation);
		transition:
			transform var(--motion-fast) var(--ease-standard),
			filter var(--motion-fast) var(--ease-standard),
			box-shadow var(--motion-fast) var(--ease-standard);

		/* A disabled primary can't just fade its gradient to 50% — that drops the white
		   label below 3:1. Use a flat, plainly-inactive fill so the label still clears
		   WCAG AA (tertiary text on the muted lane) while reading as disabled. */
		&:disabled {
			color: var(--colour-text-tertiary);
			background: var(--colour-background-muted);
			box-shadow: none;
			opacity: 1;
		}

		&:hover {
			box-shadow: var(--colour-brand-elevation-hover);
			filter: brightness(1.07);
		}

		/* Overrides the shared translateY nudge with the marketing scale press. */
		&:active:not(:disabled) {
			filter: brightness(1.07);
			transform: scale(0.97);
		}
	}

	&[data-variant='secondary'] {
		color: var(--colour-text-primary);
		background-color: var(--colour-background-surface);
		border-color: var(--colour-border-strong);

		&:hover {
			border-color: var(--colour-text-secondary);
		}
	}

	/* Between secondary and ghost: a resting surface so low-emphasis actions
	   (card quick-actions, row controls) still read as buttons, not links. */
	&[data-variant='subtle'] {
		color: var(--colour-text-secondary);
		background-color: var(--colour-background-page);
		border-color: var(--colour-border-subtle);

		&:hover {
			color: var(--colour-text-primary);
			background-color: var(--colour-background-surface);
			border-color: var(--colour-border-strong);
		}
	}

	/* Destructive tone, composable with any variant via data-tone. On the low-emphasis
	   variants (ghost / subtle / secondary) it recolours the label + border red, which
	   reads fine on their light backgrounds. */
	&[data-tone='danger'] {
		color: var(--colour-danger);

		&:hover {
			color: var(--colour-danger);
			border-color: var(--colour-danger);
		}
	}

	/* A destructive *primary* is a solid red button with a white label — not the brand
	   gradient with red text (unreadable, red on violet). Higher specificity than both
	   the primary-variant and the danger-tone blocks, so it wins the fill + label. */
	&[data-variant='primary'][data-tone='danger'] {
		color: var(--colour-text-on-brand);
		background: var(--colour-danger-gradient);
		box-shadow: 0 3px 10px color-mix(in srgb, var(--colour-danger) 28%, transparent);

		/* Same flat, legible-label disabled treatment as the plain primary. */
		&:disabled {
			color: var(--colour-text-tertiary);
			background: var(--colour-background-muted);
			box-shadow: none;
			opacity: 1;
		}

		&:hover {
			color: var(--colour-text-on-brand);
			border-color: transparent;
			filter: brightness(1.07);
		}
	}

	&[data-variant='ghost'] {
		color: var(--colour-text-primary);
		background-color: transparent;

		&:hover {
			background-color: var(--colour-background-page);
		}
	}
}
